// Literal, reviewable conversion of copied medicine text into template tokens.
// No DOM is mounted, no HTML attributes are rewritten and no content is saved.
const variables = new Set(['generic_name', 'brand_name', 'originator_brand', 'originator_company', 'company_name', 'country', 'strengths', 'dosage_form', 'pack_types', 'minimum_order_quantity', 'site_name']);
const aliases = { origin_brand: 'originator_brand', origin_company: 'originator_company' };
const blocks = new Set(['p', 'div', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'ul', 'ol', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'blockquote']);
const hidden = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'textarea', 'title', 'xmp', 'plaintext', 'svg', 'math', 'template']);
const rawHidden = new Set(['script', 'style', 'iframe', 'textarea', 'title', 'xmp', 'plaintext']);
const entities = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0', ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', reg: '®', trade: '™', copy: '©' };
const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const canonical = name => aliases[name] || name;
// Reject control characters in reviewed literal replacement values.
// eslint-disable-next-line no-control-regex
const validValue = value => typeof value === 'string' && value.trim() && value.length <= 2000 && !/[{}\u0000-\u001f\u007f]/u.test(value);
const numeric = value => /^[+-]?\d+(?:[.,]\d+)?$/u.test(value.trim());
const key = value => value.replace(/\s+/gu, ' ').trim().toLocaleLowerCase();

function inputFields(fields) {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields) || Object.keys(fields).length > 30) throw Error('Provide template text fields.');
  for (const value of Object.values(fields)) if (typeof value !== 'string' || value.length > 150000) throw Error('Template text is invalid or too large.');
  return fields;
}

function decodeEntity(token) {
  if (token[1] !== '#') return entities[token.slice(1, -1)] ?? null;
  const hex = /^&#x/iu.test(token), code = Number.parseInt(token.slice(hex ? 3 : 2, -1), hex ? 16 : 10);
  if (!Number.isInteger(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return '\ufffd';
  return String.fromCodePoint(code);
}

// Read only text ranges from HTML. Tag scanning honours quoted '>' characters;
// comments and raw/inert element contents stay opaque. The original markup is
// retained byte-for-byte, rather than parsed and reserialized by a browser.
function visible(value, rich) {
  let text = '', cursor = 0, nodeId = 0; const points = [], stack = [];
  const append = (raw, start) => {
    nodeId++;
    for (let index = 0; index < raw.length;) {
      const entity = rich && raw[index] === '&' ? /^&(?:#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/u.exec(raw.slice(index)) : null;
      const decoded = entity ? decodeEntity(entity[0]) : null;
      const chunk = decoded !== null ? decoded : String.fromCodePoint(raw.codePointAt(index)), width = decoded !== null ? entity[0].length : chunk.length;
      for (let unit = 0; unit < chunk.length; unit++) points.push({ start: start + index, end: start + index + width, node: nodeId });
      text += chunk; index += width;
    }
  };
  const boundary = () => { text += '\u0000'; points.push(null); };
  if (!rich) { append(value, 0); return { text, points }; }
  while (cursor < value.length) {
    if (rawHidden.has(stack[stack.length - 1])) {
      const closing = new RegExp(`</\\s*${stack[stack.length - 1]}\\b`, 'giu'); closing.lastIndex = cursor;
      const match = closing.exec(value); if (!match) break; cursor = match.index;
    }
    if (value.startsWith('<!--', cursor)) { const end = value.indexOf('-->', cursor + 4); cursor = end < 0 ? value.length : end + 3; continue; }
    if (value[cursor] === '<') {
      const tag = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9:-]*)\b/u.exec(value.slice(cursor));
      if (tag || /^<!|^<\?/u.test(value.slice(cursor))) {
        let end = cursor + 1, quote = '';
        for (; end < value.length; end++) { const char = value[end]; if (quote) { if (char === quote) quote = ''; } else if (char === '"' || char === "'") quote = char; else if (char === '>') break; }
        if (end >= value.length) break; // Never edit an unfinished tag/attribute.
        if (tag) {
          const name = tag[2].toLowerCase(), closing = !!tag[1];
          if (hidden.has(name)) {
            if (closing) { const at = stack.lastIndexOf(name); if (at >= 0) stack.splice(at); }
            else if (!/\/\s*>$/u.test(value.slice(cursor, end + 1))) stack.push(name);
          }
          if (blocks.has(name)) boundary();
        }
        cursor = end + 1; continue;
      }
    }
    const start = cursor; cursor++;
    while (cursor < value.length && value[cursor] !== '<') cursor++;
    if (!stack.length) append(value.slice(start, cursor), start);
  }
  return { text, points };
}

function protectedRanges(text) {
  const ranges = [];
  // NUL separates text flows internally; URL/email matches must not cross it.
  // eslint-disable-next-line no-control-regex
  for (const pattern of [/\{\{[\s\S]*?\}\}|\{[^{}]*\}/gu, /(?:https?:\/\/|ftp:\/\/|mailto:|www\.)[^\s\u0000]+/giu, /[^\s\u0000@]+@[^\s\u0000@]+\.[^\s\u0000@]+/gu, /(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,63}(?:\/[^\s\u0000]*)?/giu]) {
    for (const match of text.matchAll(pattern)) ranges.push({ start: match.index, end: match.index + match[0].length });
  }
  return ranges;
}
const word = character => !!character && /[\p{Script=Latin}\p{Script=Cyrillic}\p{Script=Arabic}\p{N}_]/u.test(character);
function literalMatches(flow, value) {
  const pattern = new RegExp(escapeRegex(value).replace(/\s+/gu, '\\s+'), 'giu'), protectedText = protectedRanges(flow.text), output = [];
  for (const match of flow.text.matchAll(pattern)) {
    const start = match.index, end = start + match[0].length;
    if (!flow.points[start] || !flow.points[end - 1] || (word(value[0]) && word(flow.text[start - 1])) || (word(value[value.length - 1]) && word(flow.text[end]))) continue;
    if (/^[+-]?\d/u.test(value) && /[\p{L}\p{N}_.+−-]/u.test(flow.text[start - 1] || '')) continue;
    if (numeric(value) && (/[%‰°]/u.test(flow.text[end] || '') || (/[.,]/u.test(flow.text[end] || '') && /\d/u.test(flow.text[end + 1] || '')))) continue;
    if (protectedText.some(range => range.start < end && range.end > start)) continue;
    output.push({ start, end });
  }
  return output;
}
const richField = (name, options) => options.richFields ? options.richFields.includes(name) : name.startsWith('section:');

function matchesForFields(fields, rows, options) {
  const output = [];
  for (const [field, value] of Object.entries(fields)) {
    const flow = visible(value, richField(field, options)), occupied = [];
    for (const row of [...rows].sort((a, b) => b.value.length - a.value.length)) {
      for (const match of literalMatches(flow, row.value)) {
        if (occupied.some(range => range.start < match.end && range.end > match.start)) continue;
        occupied.push(match); output.push({ ...match, row, field, flow });
      }
    }
  }
  return output;
}

export function suggestTemplateVariables(fields, resolvedVariables = {}, options = {}) {
  inputFields(fields); const grouped = new Map();
  for (const [inputName, supplied] of Object.entries(resolvedVariables || {})) {
    const variable = canonical(inputName), value = typeof supplied === 'number' ? String(supplied) : supplied;
    if (!variables.has(variable) || !validValue(value)) continue;
    const normalized = value.replace(/\s+/gu, ' ').trim(), id = key(normalized), row = grouped.get(id) || { value: normalized, choices: [] };
    if (!row.choices.includes(variable)) row.choices.push(variable);
    grouped.set(id, row);
  }
  const rows = [...grouped.values()].sort((a, b) => b.value.length - a.value.length), counts = new Map(rows.map(row => [row, 0]));
  for (const match of matchesForFields(fields, rows, options)) counts.set(match.row, counts.get(match.row) + 1);
  return rows.filter(row => counts.get(row)).map(row => ({ ...row, variable: row.choices.length === 1 ? row.choices[0] : null, ambiguous: row.choices.length > 1, occurrences: counts.get(row), requiresReview: numeric(row.value) }));
}

export function applyTemplateVariableMappings(fields, selectedMappings = [], options = {}) {
  inputFields(fields);
  if (!Array.isArray(selectedMappings) || selectedMappings.length > 50) throw Error('Choose up to 50 reviewed variable replacements.');
  const rows = [], seen = new Map(), skipped = [];
  for (const mapping of selectedMappings) {
    if (mapping?.selected === false) continue;
    const variable = canonical(mapping?.variable), value = mapping?.value;
    if (!validValue(value) || !variables.has(variable) || (mapping.choices && !mapping.choices.map(canonical).includes(variable))) throw Error('Choose a supported variable for every selected value.');
    if (numeric(value) && mapping.confirmed !== true && options.confirmNumeric !== true) { skipped.push({ value, variable, reason: 'numeric_confirmation_required' }); continue; }
    const id = key(value), previous = seen.get(id);
    if (previous && previous !== variable) throw Error('Choose one variable for each identical value.');
    if (previous) continue;
    seen.set(id, variable); rows.push({ value: value.replace(/\s+/gu, ' ').trim(), variable, count: 0 });
  }
  const edits = new Map(Object.keys(fields).map(field => [field, []]));
  for (const match of matchesForFields(fields, rows, options)) {
    const groups = [];
    for (const point of match.flow.points.slice(match.start, match.end)) {
      if (!point) continue;
      const last = groups[groups.length - 1];
      if (last && last.node === point.node && point.start <= last.end) last.end = Math.max(last.end, point.end);
      else groups.push({ ...point });
    }
    groups.forEach((group, index) => edits.get(match.field).push({ start: group.start, end: group.end, replacement: index ? '' : `{${match.row.variable}}` }));
    match.row.count++;
  }
  const updated = Object.fromEntries(Object.entries(fields).map(([field, value]) => {
    let output = value;
    for (const edit of edits.get(field).sort((a, b) => b.start - a.start)) output = output.slice(0, edit.start) + edit.replacement + output.slice(edit.end);
    return [field, output];
  }));
  return { fields: updated, replacements: rows.filter(row => row.count).map(({ value, variable, count }) => ({ value, variable, count })), count: rows.reduce((total, row) => total + row.count, 0), skipped };
}
